/**
 * Entries. Every shift entered on the machines this person runs — to see,
 * to edit, and to delete.
 *
 * Nothing here removes a figure from the ledger. An edit reverses the old
 * movements and posts new ones; a delete reverses them and marks the entry
 * deleted. The detail panel shows the history, so what was entered first
 * is never lost.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError } from "../../lib/api";
import { controls, type EntryDetail, type EntryRow } from "../../lib/controls";
import type { User } from "../../lib/types";
import {
  fmtDate,
  fmtPct,
  fmtQty,
  fmtShift,
  fmtStatus,
  relativeTime,
  toNumber,
} from "../../lib/format";
import { useReportPeriod } from "../../lib/reportPeriod";
import { ReportDownload } from "../../components/ReportDownload";
import { PeriodPicker } from "../../components/PeriodPicker";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { useSheet, type SheetColumn } from "../../lib/sheet";

/** Blank counts as nothing entered, not as "not a number". */
const n = (value: string | number | null | undefined) => toNumber(value) || 0;

const COLUMNS: SheetColumn<EntryRow>[] = [
  { key: "date", label: "Date", type: "date", value: (r) => r.business_date,
    format: (r) => fmtDate(r.business_date) },
  { key: "shift", label: "Shift", value: (r) => fmtShift(r.shift) },
  { key: "machine", label: "Machine", value: (r) => r.machine_name },
  { key: "hours", label: "Hours", type: "number", numeric: true,
    value: (r) => n(r.running_hours) },
  { key: "manpower", label: "Manpower", type: "number", numeric: true,
    value: (r) => r.manpower_count ?? 0 },
  { key: "kwh", label: "kWh", type: "number", numeric: true,
    value: (r) => n(r.power_units) },
  { key: "input", label: "Input kg", type: "number", numeric: true,
    value: (r) => n(r.input_qty), format: (r) => fmtQty(r.input_qty, 2) },
  { key: "output", label: "Output kg", type: "number", numeric: true,
    value: (r) => n(r.output_qty), format: (r) => fmtQty(r.output_qty, 2) },
  { key: "waste", label: "Waste kg", type: "number", numeric: true,
    value: (r) => n(r.declared_loss),
    format: (r) => fmtQty(r.declared_loss, 2) },
  { key: "unaccounted", label: "Unaccounted kg", type: "number", numeric: true,
    value: (r) => n(r.variance_qty),
    format: (r) => fmtQty(r.variance_qty, 2) },
  { key: "reason", label: "Reason", value: (r) => r.reason ?? "" },
  { key: "status", label: "Status", value: (r) => fmtStatus(r.status) },
  { key: "entered_by", label: "Entered by", value: (r) => r.entered_by ?? "" },
];

export function Entries({
  user,
  onEdit,
}: {
  user: User;
  onEdit: (id: number, kind: "SHIFT" | "LOOM") => void;
}) {
  const [period, setPeriod] = useReportPeriod();
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [rows, setRows] = useState<EntryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);

  const load = useCallback(() => {
    setRows(null);
    setError(null);
    controls
      .entries(period.from, period.to, includeDeleted, period.shift)
      .then(setRows)
      .catch((e: ApiError) => setError(e.message));
  }, [period, includeDeleted]);

  useEffect(load, [load]);

  const sheet = useSheet(rows ?? [], COLUMNS);

  const totals = useMemo(() => {
    const live = sheet.rows.filter((r) => r.status !== "DELETED");
    const sum = (f: (r: EntryRow) => number) => live.reduce((s, r) => s + f(r), 0);
    return {
      entries: live.length,
      hours: sum((r) => n(r.running_hours)),
      manpower: sum((r) => r.manpower_count ?? 0),
      kwh: sum((r) => n(r.power_units)),
      unaccounted: sum((r) => n(r.variance_qty)),
    };
  }, [sheet.rows]);

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Entries</h2>
          <p className="page__sub">
            {user.role === "OPERATOR"
              ? "Shifts on your machines. You can change the ones you entered until they are verified."
              : "Every shift entered — open one to see its lines, edit it or delete it"}
          </p>
        </div>
        <div className="page__controls">
          <PeriodPicker value={period} onChange={setPeriod} shifts />
          <ReportDownload title="Entries" period={period} />
          <label className="inputgroup inputgroup--sm">
            <span className="label">Deleted</span>
            <select
              value={includeDeleted ? "yes" : "no"}
              onChange={(e) => setIncludeDeleted(e.target.value === "yes")}
            >
              <option value="no">Hide</option>
              <option value="yes">Show</option>
            </select>
          </label>
        </div>
      </header>

      {error && <div className="banner banner--negative">{error}</div>}

      {!rows && !error && (
        <div className="state">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton state__bar" />)}
        </div>
      )}

      {rows && (
        <>
          <div className="kpirow">
            {[
              ["Entries", fmtQty(totals.entries, 0), "in the rows shown"],
              ["Hours", fmtQty(totals.hours, 1), "summed"],
              ["Manpower", fmtQty(totals.manpower, 0), "person-shifts, summed"],
              ["Power", `${fmtQty(totals.kwh, 0)} kWh`, "summed"],
              ["Unaccounted", `${fmtQty(totals.unaccounted, 1)} kg`, "summed"],
            ].map(([label, value, sub]) => (
              <div key={label} className="kpi">
                <span className="label">{label}</span>
                <span className="kpi-value num">{value}</span>
                <span className="kpi__sub">{sub}</span>
              </div>
            ))}
          </div>

          <SheetToolbar sheet={sheet} exportName="entries">
            <span className="page__note">Press a row to open the entry.</span>
          </SheetToolbar>
          {rows.length === 0 ? (
            <div className="state state--empty">
              <p>No entries in this period.</p>
            </div>
          ) : (
            <div className="tablewrap tablewrap--tall">
              <table className="table sheet">
                <SheetHead sheet={sheet} />
                <tbody>
                  {sheet.rows.map((r) => (
                    <tr
                      key={r.id}
                      className={`rowlink${r.status === "DELETED" ? " row--deleted" : ""}${
                        r.within_tolerance === false ? " row--warn" : ""
                      }`}
                      onClick={() => setSelected(r.id)}
                      tabIndex={0}
                      onKeyDown={(e) => e.key === "Enter" && setSelected(r.id)}
                    >
                      <td>{fmtDate(r.business_date)}</td>
                      <td>{fmtShift(r.shift)}</td>
                      <td>
                        {r.machine_name}
                        {r.edited && <span className="pill" title="Changed after it was entered"> edited</span>}
                      </td>
                      <td className="num mono">{fmtQty(r.running_hours, 1)}</td>
                      <td className="num mono">{r.manpower_count ?? "—"}</td>
                      <td className="num mono">{fmtQty(r.power_units, 0)}</td>
                      <td className="num mono">{fmtQty(r.input_qty, 2)}</td>
                      <td className="num mono">{fmtQty(r.output_qty, 2)}</td>
                      <td className="num mono">{fmtQty(r.declared_loss, 2)}</td>
                      <td className="num mono">
                        {fmtQty(r.variance_qty, 2)}
                        {r.variance_pct !== null && (
                          <span className="cell__unit"> {fmtPct(r.variance_pct)}</span>
                        )}
                      </td>
                      <td>{r.reason ?? ""}</td>
                      <td>{fmtStatus(r.status)}</td>
                      <td>{r.entered_by ?? ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {selected !== null && (
        <EntryPanel
          id={selected}
          onClose={() => setSelected(null)}
          onEdit={onEdit}
          onChanged={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function EntryPanel({
  id,
  onClose,
  onEdit,
  onChanged,
}: {
  id: number;
  onClose: () => void;
  onEdit: (id: number, kind: "SHIFT" | "LOOM") => void;
  onChanged: () => void;
}) {
  const [entry, setEntry] = useState<EntryDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    controls.entry(id).then(setEntry).catch((e: ApiError) => setError(e.message));
  }, [id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await controls.deleteEntry(id, reason.trim());
      onChanged();
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  const lineGroups: [string, string][] = [
    ["ISSUE", "Input"],
    ["PRODUCTION_OUT", "Output"],
    ["LOSS", "Waste"],
  ];

  return (
    <div className="drill" role="dialog" aria-modal="true" aria-label="Entry">
      <div className="drill__scrim" onClick={onClose} aria-hidden="true" />
      <div className="drill__panel">
        <header className="drill__head">
          <h3 className="drill__title">
            {entry
              ? `${entry.machine_name} · ${fmtDate(entry.business_date)} · ${fmtShift(entry.shift)}`
              : "Entry"}
          </h3>
          <div className="drill__actions">
            {entry?.can_change && !deleting && (
              <>
                <button className="btn btn--auto btn--sm"
                        onClick={() => onEdit(entry.id, entry.kind)}>
                  Edit
                </button>
                <button className="btn btn--danger btn--auto btn--sm"
                        onClick={() => setDeleting(true)}>
                  Delete
                </button>
              </>
            )}
            <button className="btn btn--ghost btn--auto btn--sm" onClick={onClose}>
              Close
            </button>
          </div>
        </header>

        {error && <div className="banner banner--negative">{error}</div>}
        {!entry && !error && <div className="skeleton state__bar" />}

        {entry && (
          <>
            {!entry.can_change && entry.cannot_change_because && (
              <p className="section-note">{entry.cannot_change_because}</p>
            )}

            {deleting && (
              <div className="banner banner--warning">
                <div className="stockwarn">
                  <strong>Delete this entry?</strong>
                  <span>
                    Its stock movements are reversed and it leaves every report.
                    The history keeps what it said.
                  </span>
                  <label className="inputgroup">
                    <span className="label">Why — required</span>
                    <input value={reason} onChange={(e) => setReason(e.target.value)}
                           placeholder="e.g. entered on the wrong machine" autoFocus />
                  </label>
                </div>
                <div className="drill__actions">
                  <button className="btn btn--danger btn--auto"
                          disabled={busy || reason.trim().length < 3} onClick={remove}>
                    {busy ? "Deleting…" : "Delete entry"}
                  </button>
                  <button className="btn btn--ghost btn--auto"
                          onClick={() => setDeleting(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <dl className="detail__grid">
              {[
                ["Status", fmtStatus(entry.status)],
                ["Hours", fmtQty(entry.running_hours, 1)],
                ["Manpower", entry.manpower_count ?? "—"],
                ["Power kWh", fmtQty(entry.power_units, 0)],
                ["Input kg", fmtQty(entry.input_qty, 2)],
                ["Output kg", fmtQty(entry.output_qty, 2)],
                ["Waste kg", fmtQty(entry.declared_loss, 2)],
                [
                  "Unaccounted",
                  `${fmtQty(entry.variance_qty, 2)} kg · ${fmtPct(entry.variance_pct)}`,
                ],
                ["Tolerance", fmtPct(toNumber(entry.tolerance_pct) * 100)],
                ["Reason", entry.reason ?? "—"],
                ["Entered by", entry.entered_by ?? "—"],
                ["Verified by", entry.approved_by_name ?? "—"],
              ].map(([label, value]) => (
                <div key={String(label)}>
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
            {entry.variance_note && <p className="section-note">Note: {entry.variance_note}</p>}

            <div className="tablewrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Line</th>
                    <th>Material</th>
                    <th className="num">Quantity</th>
                    <th>From</th>
                    <th>To</th>
                  </tr>
                </thead>
                <tbody>
                  {lineGroups.flatMap(([type, label]) =>
                    entry.lines
                      .filter((l) => l.type === type)
                      .map((l) => (
                        <tr key={l.movement_id}>
                          <td>{label}{l.from === null && type === "PRODUCTION_OUT" ? " (length)" : ""}</td>
                          <td>{l.item_name} · {l.item_code}</td>
                          <td className="num mono">{fmtQty(l.qty, 3)} {l.uom.toLowerCase()}</td>
                          <td>{l.from ?? "—"}</td>
                          <td>{l.to ?? "—"}</td>
                        </tr>
                      ))
                  )}
                  {entry.lines.length === 0 && (
                    <tr><td colSpan={5}>No live lines — this entry has been deleted.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {entry.looms.length > 0 && (
              <>
                <h4 className="section-title">Looms</h4>
                <div className="tablewrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Loom</th>
                        <th>Operator</th>
                        <th>Party</th>
                        <th className="num">Input kg</th>
                        <th className="num">Output kg</th>
                        <th className="num">Metres</th>
                        <th>Rolls</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entry.looms.map((l) => (
                        <tr key={l.loom_no}>
                          <td className="mono">{l.loom_no}</td>
                          <td>{l.operator_name ?? "—"}</td>
                          <td>{l.party_name ?? "—"}</td>
                          <td className="num mono">{fmtQty(l.input_kg, 2)}</td>
                          <td className="num mono">{fmtQty(l.output_kg, 2)}</td>
                          <td className="num mono">{fmtQty(l.qty_mtr, 0)}</td>
                          <td>
                            {l.rolls.length
                              ? l.rolls.map((r) => `${r.roll_no} (${fmtQty(r.metres, 0)} m)`).join(", ")
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <h4 className="section-title">History</h4>
            <ul className="banner__list">
              {entry.history.map((h, i) => (
                <li key={i}>
                  {h.action.toLowerCase()} by {h.user ?? "—"} · {relativeTime(h.at)}
                  {h.reason ? ` — “${h.reason}”` : ""}
                </li>
              ))}
              {entry.history.length === 0 && <li>Entered before changes were recorded.</li>}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
